/**
 * Featured scorecard carousel — the landing-page rotation.
 *
 * MECHANICAL PORT of the legacy carousel section, with one structural change: it no
 * longer reaches back into the query layer. The legacy code called `runQuery` and
 * `scrollToQueryPanel` directly, which in a module world is a circular import, so the
 * carousel is now handed those as hooks at init time.
 *
 * Cycles six reference assets chosen to demonstrate all three eligibility states.
 */
import type { Asset } from '../../core/types.ts';
import { ASSETS } from '../../core/universe.ts';
import { watermarkFor } from './share-card.ts';

export interface FeaturedHooks {
  runQuery: (query: string) => void;
  scrollToQueryPanel: () => void;
  pushHistory: (query: string) => void;
  /** Render the full cached universe as a sortable table. */
  renderUniverse: (assets: readonly Asset[], label: string) => void;
}

let hooks: FeaturedHooks | null = null;

/** Six reference assets spanning Eligible / Conditional / Not Eligible. */
const FEATURED_CODES = ['EQ-KSA-001', 'EQ-BRA-001', 'EQ-UAE-008', 'EQ-MYS-001', 'EQ-KSA-006', 'EQ-VNM-001'] as const;
let featuredIdx = 0;
let featuredTimer: ReturnType<typeof setInterval> | null = null;

function statusKey(s: string | undefined): string {
  if (s === 'Eligible') return 'eligible';
  if (s === 'Conditional') return 'conditional';
  return 'notEligible';
}
/** Identity, matching the declaration that won at runtime — see share-card.ts. */
function statusLabel(s: string | undefined): string {
  return s ?? '';
}
function barClassF(v: number): string {
  if (v >= 4) return 'high';
  if (v >= 3) return 'mid';
  return 'low';
}

/** Hide the landing rotation once real results are on screen. */
export function hideFeatured(): void {
  document.getElementById('featured-rotation')?.classList.add('hidden');
}

function buildShortCommentary(asset: Asset): string {
  // Use the asset's headline if available, otherwise a derived summary
  if (asset.headline) {
    // Trim to first sentence(s) for compact card display
    const trimmed = asset.headline.split('. ').slice(0, 2).join('. ').trim();
    return trimmed + (trimmed.endsWith('.') ? '' : '.');
  }
  return `${asset.name} composite of ${asset.score}/100. Sleeve: ${asset.sleeve}. Distribution: ${asset.tier_primary || '—'}.`;
}

function renderFeaturedCardInner(asset: Asset): string {
  const sk = statusKey(asset.status);
  const principles = asset.principle_scores.map((v, i) => {
    const labels = ["P1 Scale", "P2 Return", "P3 Access", "P4 DeFi Fit", "P5 Net-Return", "P6 Coverage"];
    return `
      <div class="feature-principle-row">
        <div class="label"><span>${labels[i]}</span><span class="value">${v.toFixed(1)}</span></div>
        <div class="feature-bar-track"><div class="feature-bar-fill ${barClassF(v)}" style="width: ${(v / 5) * 100}%"></div></div>
      </div>`;
  }).join("");

  const shariaFlag = asset.sharia ? `<span>·</span><span class="sharia-flag">Sharia-Eligible</span>` : "";
  const overrideTag = asset.override ? `<span class="override-tag">+ Override</span>` : "";
  const tierLine = asset.tier_secondary ? `${asset.tier_primary} → ${asset.tier_secondary}` : (asset.tier_primary || "—");
  const ticker = (asset.tickers && asset.tickers[0]) || asset.code;
  const exchange = asset.exchange_full || asset.exchange || "";
  const isin = asset.isin || "";
  const asOf = (asset.fact_sheet && asset.fact_sheet.as_of) || "Q1 2026";
  const sleeve = asset.sleeve || "—";

  return `
    <div class="feature-card" data-code="${asset.code}">
      <div class="feature-card-strip ${sk}"></div>
      <div class="feature-card-top">
        <div class="feature-corner">${asset.code}</div>
        <div class="feature-pill ${sk}"><span class="dot"></span>${statusLabel(asset.status)}</div>
      </div>
      <div class="feature-score-block">
        <div class="feature-score ${sk}">${asset.score}</div>
        <div class="feature-watermark">${watermarkFor(sk)}</div>
      </div>
      <div class="feature-info">
        <div class="feature-name">${asset.name}</div>
        <div class="feature-exchange-row">
          <span class="ticker">${ticker}</span>
          <span>·</span>
          <span>${exchange}</span>
          ${shariaFlag}
        </div>
      </div>
      <div class="feature-principles">${principles}</div>
      <div class="feature-routing">
        <div class="left"><strong>${sleeve}</strong> sleeve${overrideTag}</div>
        <div class="right">${tierLine}</div>
      </div>
      <div class="feature-commentary">${buildShortCommentary(asset)}</div>
      <div class="feature-footer">
        <span class="isin">${isin}</span>
        <span>AS OF ${asOf}</span>
      </div>
    </div>`;
}

function initFeaturedStage(): void {
  const stage = document.getElementById("featuredStage");
  if (!stage) return;
  FEATURED_CODES.forEach((code, i) => {
    const asset = ASSETS.find(a => a.code === code);
    if (!asset) { console.warn("Featured asset not found:", code); return; }
    const slot = document.createElement("div");
    slot.className = "card-slot hidden";
    slot.dataset.idx = String(i);
    slot.innerHTML = renderFeaturedCardInner(asset);
    stage.appendChild(slot);
  });
  updateFeaturedPositions();
}

function updateFeaturedPositions(): void {
  const n = FEATURED_CODES.length;
  const leftIdx = (featuredIdx - 1 + n) % n;
  const rightIdx = (featuredIdx + 1) % n;
  document.querySelectorAll<HTMLElement>("#featuredStage .card-slot").forEach(slot => {
    const idx = parseInt(slot.dataset["idx"] ?? "-1", 10);
    slot.classList.remove("left", "center", "right", "hidden");
    if (idx === featuredIdx) slot.classList.add("center");
    else if (idx === leftIdx) slot.classList.add("left");
    else if (idx === rightIdx) slot.classList.add("right");
    else slot.classList.add("hidden");
  });
  const counter = document.getElementById("featuredCounter");
  if (counter) counter.textContent = String(featuredIdx + 1).padStart(2, "0");
  document.querySelectorAll(".feature-dot").forEach((d, i) => d.classList.toggle("active", i === featuredIdx));
}

function buildFeaturedDots(): void {
  const wrap = document.getElementById("featuredDots");
  if (!wrap) return;
  wrap.innerHTML = "";
  FEATURED_CODES.forEach((code, i) => {
    const b = document.createElement("button");
    b.className = "feature-dot" + (i === 0 ? " active" : "");
    b.type = "button";
    b.setAttribute("aria-label", "Show " + code);
    b.addEventListener("click", () => { featuredIdx = i; updateFeaturedPositions(); restartFeaturedAuto(); });
    wrap.appendChild(b);
  });
}

function advanceFeatured(): void { featuredIdx = (featuredIdx + 1) % FEATURED_CODES.length; updateFeaturedPositions(); }
function startFeaturedAuto(): void { stopFeaturedAuto(); featuredTimer = setInterval(advanceFeatured, 5000); }
function stopFeaturedAuto(): void { if (featuredTimer) { clearInterval(featuredTimer); featuredTimer = null; } }
function restartFeaturedAuto(): void { stopFeaturedAuto(); startFeaturedAuto(); }

function wireFeaturedCTAs(): void {
  // Check the Full Details: runs a single-asset query using the focused card's primary ticker.
  // runQuery handles history push, render, and scroll.
  const checkBtn = document.getElementById("featuredCheckDetails");
  if (checkBtn) {
    checkBtn.addEventListener("click", () => {
      const code = FEATURED_CODES[featuredIdx];
      const asset = ASSETS.find(a => a.code === code);
      if (!asset) return;
      const ticker = (asset.tickers && asset.tickers[0]) || asset.code;
      const input = document.getElementById("search-input") as HTMLInputElement | null;
      if (input) input.value = ticker;
      hooks!.runQuery(ticker);
    });
  }
  // Explore the Universe: renders the full cache as a sortable table.
  // Bypasses parsePrompt to guarantee all 61 cached assets are shown.
  const exploreBtn = document.getElementById("featuredExploreUniverse");
  if (exploreBtn) {
    exploreBtn.addEventListener("click", () => {
      const queryLabel = "Atlas-Public universe \u00b7 all cached assets";
      const input = document.getElementById("search-input") as HTMLInputElement | null;
      if (input) input.value = queryLabel;
      hooks!.pushHistory(queryLabel);
      hooks!.renderUniverse(ASSETS, queryLabel);
      hooks!.scrollToQueryPanel();
    });
  }
}

// ============ NAVIGATION HELPERS (Shot 4 polish) ============

// Logo click → return to landing state. Hides results, shows featured carousel,
// clears search input, scrolls to top, resumes carousel auto-rotation.
function goToLanding(): void {
  const resultsSection = document.getElementById("results-section");
  if (resultsSection) resultsSection.classList.add("hidden");
  const results = document.getElementById("results");
  if (results) results.innerHTML = "";
  const featured = document.getElementById("featured-rotation");
  if (featured) featured.classList.remove("hidden");
  const input = document.getElementById("search-input") as HTMLInputElement | null;
  if (input) { input.value = ""; }
  window.scrollTo({ top: 0, behavior: "smooth" });
  startFeaturedAuto();
}

function wireHomeLogo(): void {
  const logo = document.getElementById("home-logo");
  if (!logo) return;
  logo.addEventListener("click", goToLanding);
  logo.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToLanding(); }
  });
}

// Carousel side-card click → navigate. Event delegation on the stage container
// so the handler reads the slot class at click time (which changes as cards rotate).
function wireCardNav(): void {
  const stage = document.getElementById("featuredStage");
  if (!stage) return;
  stage.addEventListener("click", (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    // Don't intercept clicks on CTA buttons or other interactive elements
    if (target.closest(".featured-cta, .featured-cta-outline, .feature-dot, button")) return;
    const slot = target.closest(".card-slot");
    if (!slot) return;
    if (slot.classList.contains("left")) {
      featuredIdx = (featuredIdx - 1 + FEATURED_CODES.length) % FEATURED_CODES.length;
      updateFeaturedPositions();
      restartFeaturedAuto();
    } else if (slot.classList.contains("right")) {
      featuredIdx = (featuredIdx + 1) % FEATURED_CODES.length;
      updateFeaturedPositions();
      restartFeaturedAuto();
    }
    // Center slot click does nothing — user uses the CTA buttons or dots
  });
}

export function initFeaturedCarousel(injected: FeaturedHooks): void {
  hooks = injected;
  const stage = document.getElementById("featuredStage");
  if (!stage) return;
  buildFeaturedDots();
  initFeaturedStage();
  wireFeaturedCTAs();
  wireCardNav();
  wireHomeLogo();
  stage.addEventListener("mouseenter", stopFeaturedAuto);
  stage.addEventListener("mouseleave", startFeaturedAuto);
  startFeaturedAuto();
}

// Defer carousel init until ASSETS is fully populated and DOM is ready
